import { useState } from 'react';
import { recentBookings } from '../../../mocks/dashboard';
import { agencyInfo } from '../../../mocks/dashboard';

const brand = agencyInfo.brandColor;

const statusConfig: Record<string, { bg: string; text: string }> = {
  Confirmed: { bg: 'bg-green-50', text: 'text-green-600' },
  Pending: { bg: 'bg-amber-50', text: 'text-amber-600' },
  Cancelled: { bg: 'bg-red-50', text: 'text-red-500' },
};

const payConfig: Record<string, { bg: string; text: string }> = {
  Paid: { bg: 'bg-green-50', text: 'text-green-600' },
  Partial: { bg: 'bg-amber-50', text: 'text-amber-600' },
  Unpaid: { bg: 'bg-red-50', text: 'text-red-500' },
  Refunded: { bg: 'bg-gray-100', text: 'text-gray-500' },
};

export default function RecentBookings() {
  const [filter, setFilter] = useState('All');
  const filters = ['All', 'Confirmed', 'Pending', 'Cancelled'];

  const filtered =
    filter === 'All' ? recentBookings : recentBookings.filter((b) => b.status === filter);

  return (
    <div className="bg-white rounded-2xl border border-gray-100">
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-50">
        <div>
          <h2 className="text-base font-semibold text-gray-800">Recent Bookings</h2>
          <p className="text-xs text-gray-400 mt-0.5">Last 30 days activity</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex gap-1 bg-gray-100 p-1 rounded-full">
            {filters.map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap transition-all cursor-pointer ${
                  filter === f ? 'bg-white text-gray-800' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {f}
              </button>
            ))}
          </div>
          <button
            className="flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap transition-colors"
          >
            <i className="ri-download-2-line" /> Export
          </button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-50">
              <th className="text-left text-xs font-semibold text-gray-400 uppercase tracking-wider px-5 py-3">Booking</th>
              <th className="text-left text-xs font-semibold text-gray-400 uppercase tracking-wider px-3 py-3">Customer</th>
              <th className="text-left text-xs font-semibold text-gray-400 uppercase tracking-wider px-3 py-3">Service</th>
              <th className="text-left text-xs font-semibold text-gray-400 uppercase tracking-wider px-3 py-3">Travel Date</th>
              <th className="text-left text-xs font-semibold text-gray-400 uppercase tracking-wider px-3 py-3">Status</th>
              <th className="text-left text-xs font-semibold text-gray-400 uppercase tracking-wider px-3 py-3">Payment</th>
              <th className="text-right text-xs font-semibold text-gray-400 uppercase tracking-wider px-5 py-3">Amount</th>
              <th className="px-3 py-3" />
            </tr>
          </thead>
          <tbody>
            {filtered.map((b, i) => (
              <tr
                key={b.id}
                className={`border-b border-gray-50 hover:bg-gray-50/50 transition-colors ${
                  i % 2 === 0 ? '' : 'bg-gray-50/20'
                }`}
              >
                <td className="px-5 py-3.5">
                  <a
                    href={`/bookings/${b.id}`}
                    className="text-sm font-mono font-semibold cursor-pointer hover:underline"
                    style={{ color: brand }}
                  >
                    {b.id}
                  </a>
                </td>
                <td className="px-3 py-3.5">
                  <div className="flex items-center gap-2.5">
                    <div
                      className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                      style={{ backgroundColor: brand }}
                    >
                      {b.avatar}
                    </div>
                    <span className="text-sm text-gray-700 font-medium whitespace-nowrap">{b.customer}</span>
                  </div>
                </td>
                <td className="px-3 py-3.5">
                  <div className="flex items-center gap-1.5">
                    <i className={`${b.serviceIcon} text-gray-400 text-sm`} />
                    <span className="text-sm text-gray-600 whitespace-nowrap">{b.service}</span>
                  </div>
                </td>
                <td className="px-3 py-3.5">
                  <div className="flex items-center gap-1.5 text-sm text-gray-600 whitespace-nowrap">
                    <i className="ri-calendar-line text-gray-400 text-xs" />
                    {b.travelDate}
                  </div>
                </td>
                <td className="px-3 py-3.5">
                  <span
                    className={`text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap ${
                      statusConfig[b.status]?.bg || ''
                    } ${statusConfig[b.status]?.text || ''}`}
                  >
                    {b.status}
                  </span>
                </td>
                <td className="px-3 py-3.5">
                  <span
                    className={`text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap ${
                      payConfig[b.payStatus]?.bg || ''
                    } ${payConfig[b.payStatus]?.text || ''}`}
                  >
                    {b.payStatus}
                  </span>
                </td>
                <td className="px-5 py-3.5 text-right">
                  <span className="text-sm font-bold text-gray-800 whitespace-nowrap">{b.amount}</span>
                </td>
                <td className="px-3 py-3.5">
                  <button className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 cursor-pointer">
                    <i className="ri-more-2-fill text-sm" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between px-5 py-4 border-t border-gray-50">
        <p className="text-xs text-gray-400">Showing {filtered.length} of {recentBookings.length} bookings</p>
        <div className="flex items-center gap-1">
          <button className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-100 text-gray-400 hover:bg-gray-50 cursor-pointer">
            <i className="ri-arrow-left-s-line text-sm" />
          </button>
          {[1, 2, 3].map((p) => (
            <button
              key={p}
              className={`w-8 h-8 flex items-center justify-center rounded-lg text-xs font-medium cursor-pointer ${
                p === 1 ? 'text-white' : 'text-gray-500 hover:bg-gray-50 border border-gray-100'
              }`}
              style={p === 1 ? { backgroundColor: brand } : {}}
            >
              {p}
            </button>
          ))}
          <button className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-100 text-gray-400 hover:bg-gray-50 cursor-pointer">
            <i className="ri-arrow-right-s-line text-sm" />
          </button>
        </div>
      </div>
    </div>
  );
}
